import { useState } from 'react'
import { Bell, Plus, Trash2, ToggleLeft, ToggleRight, Send } from 'lucide-react'
import { useCustomers } from '../hooks/useCustomers'
import { useAlertRules, useCreateAlertRule, useUpdateAlertRule, useDeleteAlertRule, useTestAlertRule } from '../hooks/useAlertRules'

const CHANNEL_OPTIONS = [
  { value: 'email', label: 'Email' },
  { value: 'teams', label: 'Teams Webhook' },
]

const ALERT_TYPE_OPTIONS = [
  { value: 'failure', label: 'Failure' },
  { value: 'trial_expiry', label: 'Trial expiry' },
  { value: 'overdue', label: 'Overdue scheduled item' },
]

const ALERT_TYPE_LABELS = {
  failure: 'Failure',
  trial_expiry: 'Trial expiry',
  overdue: 'Overdue',
}

const emptyForm = { customerId: '', alertTypes: ['failure'], workspacePattern: '', alertChannel: 'email', channelTarget: '' }

const showsWorkspacePattern = (alertTypes) => alertTypes.some(t => t === 'failure' || t === 'overdue')

export default function AlertRules() {
  const [form, setForm] = useState(emptyForm)
  const [formError, setFormError] = useState(null)
  const [testingRule, setTestingRule] = useState(null)
  const [testResult, setTestResult] = useState({})

  const { data: customers = [] } = useCustomers()
  const { data: rules = [], isLoading, error } = useAlertRules()
  const createRule = useCreateAlertRule()
  const updateRule = useUpdateAlertRule()
  const deleteRule = useDeleteAlertRule()
  const testRule = useTestAlertRule()

  const handleToggle = (rule) => {
    updateRule.mutate({
      id: rule.id,
      data: {
        workspacePattern: rule.workspacePattern,
        alertChannel: rule.alertChannel,
        channelTarget: rule.channelTarget,
        isEnabled: !rule.isEnabled,
        alertType: rule.alertType,
      },
    })
  }

  const handleDelete = (id) => {
    if (!confirm('Delete this alert rule?')) return
    deleteRule.mutate(id)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setFormError(null)
    if (!form.customerId || !form.channelTarget) {
      setFormError('Customer and target are required')
      return
    }
    if (form.alertTypes.length === 0) {
      setFormError('Select at least one alert type')
      return
    }
    try {
      await createRule.mutateAsync({
        customerId: form.customerId,
        alertType: form.alertTypes.join(','),
        workspacePattern: showsWorkspacePattern(form.alertTypes) ? (form.workspacePattern || null) : null,
        alertChannel: form.alertChannel,
        channelTarget: form.channelTarget,
      })
      setForm(emptyForm)
    } catch (e) {
      setFormError(e.message)
    }
  }

  const handleTest = async (rule) => {
    setTestingRule(rule.id)
    setTestResult(prev => ({ ...prev, [rule.id]: null }))
    try {
      await testRule.mutateAsync(rule.id)
      setTestResult(prev => ({ ...prev, [rule.id]: { status: 'sent' } }))
    } catch (e) {
      setTestResult(prev => ({ ...prev, [rule.id]: { status: 'error', message: e.message } }))
    } finally {
      setTestingRule(null)
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
        <Bell size={22} style={{ color: 'var(--accent)' }} />
        <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600' }}>Alert Rules</h1>
      </div>
      <p style={{ margin: '0 0 0.5rem', color: 'var(--gray-600)', fontSize: '14px' }}>
        Send email or Teams notifications on failures, trial expiries, and overdue scheduled items.
      </p>

      {/* New rule bar */}
      <form onSubmit={handleSubmit}>
        <div className="inventory-filters" style={{ flexWrap: 'wrap' }}>
          <div className="filter-group">
            <label>Customer:</label>
            <select
              value={form.customerId}
              onChange={e => setForm(f => ({ ...f, customerId: e.target.value }))}
              required
            >
              <option value="">Select customer...</option>
              {customers.map(c => (
                <option key={c.tenant_id} value={c.tenant_id}>{c.customer_name}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Alert on:</label>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
              {ALERT_TYPE_OPTIONS.map(o => (
                <label key={o.value} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontWeight: 400, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  <input
                    type="checkbox"
                    checked={form.alertTypes.includes(o.value)}
                    onChange={e => {
                      const next = e.target.checked
                        ? [...form.alertTypes, o.value]
                        : form.alertTypes.filter(t => t !== o.value)
                      setForm(f => ({ ...f, alertTypes: next }))
                    }}
                  />
                  {o.label}
                </label>
              ))}
            </div>
          </div>

          <div className="filter-group">
            <label>Channel:</label>
            <select
              value={form.alertChannel}
              onChange={e => setForm(f => ({ ...f, alertChannel: e.target.value, channelTarget: '' }))}
            >
              {CHANNEL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          {showsWorkspacePattern(form.alertTypes) && (
            <div className="filter-group">
              <label>Workspace pattern:</label>
              <input
                type="text"
                placeholder="* (all)"
                value={form.workspacePattern}
                onChange={e => setForm(f => ({ ...f, workspacePattern: e.target.value }))}
                style={{ width: '130px' }}
              />
            </div>
          )}

          <div className="filter-group" style={{ flex: 1 }}>
            <label>{form.alertChannel === 'email' ? 'Email address:' : 'Webhook URL:'}</label>
            <input
              type={form.alertChannel === 'email' ? 'email' : 'url'}
              placeholder={form.alertChannel === 'email' ? 'alerts@example.com' : 'https://...'}
              value={form.channelTarget}
              onChange={e => setForm(f => ({ ...f, channelTarget: e.target.value }))}
              required
              style={{ width: '100%', minWidth: '200px' }}
            />
          </div>

          <button
            type="submit"
            className="btn-primary"
            disabled={createRule.isPending}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', whiteSpace: 'nowrap' }}
          >
            <Plus size={16} />
            {createRule.isPending ? 'Adding...' : 'Add Rule'}
          </button>
        </div>

        {formError && (
          <p style={{ color: 'var(--danger)', fontSize: '0.875rem', margin: '-8px 0 12px' }}>{formError}</p>
        )}
      </form>

      {error && (
        <div className="card" style={{ marginBottom: '1rem', padding: '0.75rem 1rem', borderLeft: '3px solid var(--danger)', color: 'var(--danger)', fontSize: '0.875rem' }}>
          {error.message}
        </div>
      )}

      {isLoading ? (
        <div className="loading">Loading...</div>
      ) : rules.length === 0 ? (
        <div className="card" style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
          <Bell size={32} style={{ marginBottom: '0.75rem', opacity: 0.3 }} />
          <p style={{ margin: 0 }}>No alert rules yet. Add one above to start receiving notifications.</p>
        </div>
      ) : (
        <div className="card" style={{ overflow: 'hidden', padding: 0 }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Alert on</th>
                <th>Workspace Pattern</th>
                <th>Channel</th>
                <th>Target</th>
                <th>Enabled</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rules.map(rule => (
                <tr key={rule.id}>
                  <td style={{ fontWeight: '500' }}>{rule.customerName}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                      {(rule.alertType || 'failure').split(',').map(t => (
                        <span key={t} style={{ fontSize: '0.75rem', padding: '1px 8px', borderRadius: '12px', background: 'var(--accent-light, #eff6ff)', color: 'var(--accent)', fontWeight: 500 }}>
                          {ALERT_TYPE_LABELS[t.trim()] || t.trim()}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td>
                    {showsWorkspacePattern((rule.alertType || 'failure').split(',')) ? (
                      <code style={{ fontSize: '0.8rem', background: 'var(--bg-page)', padding: '2px 8px', borderRadius: '4px' }}>
                        {rule.workspacePattern || '*'}
                      </code>
                    ) : (
                      <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>—</span>
                    )}
                  </td>
                  <td style={{ textTransform: 'capitalize' }}>{rule.alertChannel}</td>
                  <td style={{ maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                    {rule.channelTarget}
                  </td>
                  <td>
                    <button
                      onClick={() => handleToggle(rule)}
                      title={rule.isEnabled ? 'Click to disable' : 'Click to enable'}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', color: rule.isEnabled ? 'var(--accent)' : 'var(--text-muted)' }}
                    >
                      {rule.isEnabled ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
                    </button>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      <button
                        onClick={() => handleTest(rule)}
                        disabled={testingRule === rule.id}
                        title={testResult[rule.id]?.status === 'error' ? testResult[rule.id].message : testResult[rule.id]?.status === 'sent' ? 'Sent!' : 'Send test notification'}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: testResult[rule.id]?.status === 'sent' ? 'var(--success)' : testResult[rule.id]?.status === 'error' ? 'var(--danger)' : 'var(--text-muted)', padding: '4px', display: 'flex', borderRadius: '4px' }}
                        onMouseEnter={e => { if (testingRule !== rule.id) e.currentTarget.style.color = 'var(--accent)' }}
                        onMouseLeave={e => { if (testingRule !== rule.id) e.currentTarget.style.color = testResult[rule.id]?.status === 'sent' ? 'var(--success)' : testResult[rule.id]?.status === 'error' ? 'var(--danger)' : 'var(--text-muted)' }}
                      >
                        <Send size={15} />
                      </button>
                      {testResult[rule.id]?.status === 'error' && (
                        <span style={{ fontSize: '0.7rem', color: 'var(--danger)', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={testResult[rule.id].message}>
                          {testResult[rule.id].message}
                        </span>
                      )}
                      <button
                        onClick={() => handleDelete(rule.id)}
                        title="Delete rule"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '4px', display: 'flex', borderRadius: '4px' }}
                        onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                        onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
